import {
  asyncScheduler,
  concatMap,
  EMPTY,
  filter,
  map,
  Observable,
  scheduled,
  withLatestFrom,
} from 'rxjs';
import { ofType } from 'redux-observable';
import { navigationSlice } from './';
import { careCuesSlice } from '../careCues';
import { selectNavigation } from './selectors';
import { RootState } from '@ax/redux-store';
import { authSlice } from '../auth';
import { history } from './history';
import { toRouterPath } from './utilities';
import { NavigationPages, NotePageSections } from '@ax/constants';

type NavigationAction =
  | ReturnType<typeof navigationSlice.actions.pageNavigationIgnored>
  | ReturnType<typeof navigationSlice.actions.unsavedChangesModalOpened>;

export const onPageNavigationRequested = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(navigationSlice.actions.pageNavigationRequested.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      const navigationState = selectNavigation(state);
      if (navigationState.unsavedChanges) {
        let navigationActions: NavigationAction[] = [];

        if (navigationState.unsavedChangesSource === 'recording') {
          navigationActions = [navigationSlice.actions.unsavedChangesModalOpened()];
        }

        navigationActions.push(
          navigationSlice.actions.pageNavigationIgnored({ ...action.payload }),
        );

        return scheduled(navigationActions, asyncScheduler);
      }

      if (navigationState.ignoredNavigation?.page === NavigationPages.Login) {
        return scheduled([authSlice.actions.logoutRequested()], asyncScheduler);
      }

      history.push(toRouterPath(action.payload.page, action.payload.params));

      return EMPTY;
    }),
  );

export const onSectionNavigationRequested = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(navigationSlice.actions.sectionNavigationRequested.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      const navigationState = selectNavigation(state);

      if (action.payload.section === navigationState.currentSection) {
        return scheduled(
          [navigationSlice.actions.sectionNavigationIgnored({ ...action.payload })],
          asyncScheduler,
        );
      }

      return [
        navigationSlice.actions.sectionNavigationSucceeded({
          ...action.payload,
        }),
      ];
    }),
  );

export const reloadCareCues = (action$: Observable<any>) =>
  action$.pipe(
    ofType(navigationSlice.actions.sectionNavigationSucceeded),
    filter(
      (action) => action.payload.section === NotePageSections.CareCues && action.payload.noteId,
    ),
    map((action) => {
      return careCuesSlice.actions.loadCareCuesRequested({
        noteId: action.payload.noteId,
        isNavigation: true,
      });
    }),
  );

export const onUnsavedChangesModalConfirmed = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(navigationSlice.actions.unsavedChangesModalConfirmed.type),
    withLatestFrom(state$),
    concatMap(([_, state]) => {
      const navigationState = selectNavigation(state);
      // If no ignoredNavigation then return
      if (!navigationState.ignoredNavigation) {
        return EMPTY;
      }

      return scheduled(
        [
          navigationSlice.actions.pageNavigationRequested({
            page: navigationState.ignoredNavigation?.page,
          }),
        ],
        asyncScheduler,
      );
    }),
  );

export default [
  onPageNavigationRequested,
  onSectionNavigationRequested,
  reloadCareCues,
  onUnsavedChangesModalConfirmed,
];
